import apiClient from './meta-client';
import type { FunnelStatsDetails, FunnelStatsNode } from '@/types/funnel';

/**
 * Analytics do Bot V1 — cliente das rotas GET /meta/analytics-bot/*.
 * Contrato: claude-memory/projects/github.com_rocket-ads_ms-meta-api/docs/CONTRATO-analytics-do-bot-v1.md
 *
 * Dinheiro vem em CENTAVOS inteiros (USD); RPC/RPS em dólar (float, 4 casas);
 * taxas já em 0-100. `null` = dado inexistente, nunca zero.
 */

export type PeriodoBot = 'hoje' | 'ontem' | '7d' | '14d' | '30d' | 'mes' | 'custom';
export type CanalBot = 'ambos' | 'flow' | 'broadcast';
export type DimensaoRecorte = 'pagina' | 'pais' | 'dominio';
export type GranularidadeSerie = 'auto' | 'hora' | 'dia';

export interface FiltrosBot {
    periodo: PeriodoBot;
    /** AAAA-MM-DD, só com periodo=custom. */
    de?: string;
    ate?: string;
    pagina_id?: string | null;
    pais?: string | null;
    dominio?: string | null;
    user_id?: number | null;
}

// ── /resumo ──────────────────────────────────────────────────────────────────

export interface PeriodoInfo {
    rotulo: string;
    de: string;
    ate: string;
    dias: number;
    dia_parcial: boolean;
    hora_corrente: number | null;
}

export interface CanalResumo {
    enviadas: number | null;
    entregues: number | null;
    taxa_entrega_pct: number | null;
    cliques: number | null;
    clicadores_unicos: number | null;
    sessoes: number | null;
    taxa_sessao_pct: number | null;
    ctr_pct: number | null;
    receita: number | null;
    rpc: number | null;
    rps: number | null;
}

export interface ResumoBot {
    periodo: PeriodoInfo;
    receita: {
        total: number | null;
        flow: number | null;
        broadcast: number | null;
        total_anterior: number | null;
        delta_pct: number | null;
    };
    cliques: { total: number | null; flow: number | null; broadcast: number | null };
    canais: { flow: CanalResumo; broadcast: CanalResumo };
    avisos: string[];
    /** true enquanto o backend ainda calcula algum dia em background. */
    dados_pendentes?: boolean;
}

// ── /serie ───────────────────────────────────────────────────────────────────

export interface PontoSerie {
    rot: string;
    flow: number | null;
    broadcast: number | null;
}

export interface SerieBot {
    granularidade: 'hora' | 'dia';
    pontos: PontoSerie[];
    avisos: string[];
    /** true enquanto o backend ainda calcula algum dia em background. */
    dados_pendentes?: boolean;
}

// ── /funis ───────────────────────────────────────────────────────────────────

export interface FunilBot {
    id: number;
    nome: string;
    tipo: 'sequencia' | 'onboarding';
    paginas: { page_id: string; nome: string }[];
    rastreio_receita: boolean;
    entrantes: number | null;
    entregues: number | null;
    cliques: number | null;
    clicadores_unicos: number | null;
    ctr_pct: number | null;
    conclusao_pct: number | null;
    receita: number | null;
    rpc: number | null;
    sessoes: number | null;
    rps: number | null;
}

export interface FunisBot {
    funis: FunilBot[];
    receita_flow_total: number | null;
    receita_nao_atribuida: number | null;
    avisos: string[];
    /** true enquanto o backend ainda calcula algum dia em background. */
    dados_pendentes?: boolean;
}

// ── /broadcasts ──────────────────────────────────────────────────────────────

export type StatusBroadcastBot = 'enviado' | 'enviando' | 'pendente' | 'falhou' | 'recorrente';

export interface BotaoBroadcastBot {
    id: number;
    label: string;
    cliques: number | null;
    ctr_pct: number | null;
}

export interface BroadcastBot {
    id: number;
    nome: string;
    hora: string;
    status: StatusBroadcastBot;
    ocorrencias: number;
    enviados: number | null;
    entregues: number | null;
    falhas: number | null;
    taxa_entrega_pct: number | null;
    cliques: number | null;
    clicadores_unicos: number | null;
    ctr_pct: number | null;
    receita: number | null;
    rpc: number | null;
    sessoes: number | null;
    rps: number | null;
    copy: {
        texto: string | null; imagem_url: string | null; titulo: string | null; subtitulo: string | null;
        /** Criativa inteira do grupo, um item por bloco, na ordem do Messenger. */
        blocos?: { tipo: string | null; titulo: string | null; subtitulo: string | null; texto: string | null;
            imagem_url: string | null; botoes: (string | null)[] }[];
    } | null;
    botoes: BotaoBroadcastBot[];
}

export interface BroadcastsBot {
    broadcasts: BroadcastBot[];
    receita_broadcast_total: number | null;
    receita_nao_atribuida: number | null;
    avisos: string[];
    /** true enquanto o backend ainda calcula algum dia em background. */
    dados_pendentes?: boolean;
}

export interface QuebraLinha {
    nome: string;
    page_id?: string;
    codigo?: string;
    cliques: number | null;
    entregues?: number | null;
    receita: number | null;
}

export interface QuebrasBroadcast {
    paginas: QuebraLinha[];
    paises: QuebraLinha[];
    dominios: QuebraLinha[];
    avisos: string[];
    /** true enquanto o backend ainda calcula algum dia em background. */
    dados_pendentes?: boolean;
}

// ── /recortes ────────────────────────────────────────────────────────────────

export interface RecorteLinha {
    nome: string;
    chave: string;
    enviados: number | null;
    entregues: number | null;
    taxa_entrega_pct: number | null;
    cliques: number | null;
    sessoes: number | null;
    ctr_pct: number | null;
    receita: number | null;
    receita_flow: number | null;
    receita_broadcast: number | null;
    rpc: number | null;
    rps: number | null;
}

export interface RecortesBot {
    linhas: RecorteLinha[];
    avisos: string[];
    /** true enquanto o backend ainda calcula algum dia em background. */
    dados_pendentes?: boolean;
}

// ── Detalhe do funil (rota existente /funnels/{id}/stats/details) ────────────

/** Campos que o contrato acrescenta em stats/details; opcionais até o backend subir. */
export type FunnelStatsNodeBot = FunnelStatsNode & {
    node_type_id?: number | null;
    delay_seconds?: number | null;
};
export type FunnelStatsDetailsBot = Omit<FunnelStatsDetails, 'nodes'> & {
    is_sequence?: boolean | null;
    nodes: FunnelStatsNodeBot[];
};

// ── chamadas ─────────────────────────────────────────────────────────────────

function toQuery(f: FiltrosBot, extra?: Record<string, unknown>): Record<string, unknown> {
    const q: Record<string, unknown> = { periodo: f.periodo };
    if (f.periodo === 'custom') {
        q.de = f.de;
        q.ate = f.ate;
    }
    if (f.pagina_id) q.pagina_id = f.pagina_id;
    if (f.pais) q.pais = f.pais;
    if (f.dominio) q.dominio = f.dominio;
    if (typeof f.user_id === 'number') q.user_id = f.user_id;
    return { ...q, ...(extra ?? {}) };
}

// O contrato mostra o corpo cru; outras rotas do ms-meta-api embrulham em
// {success, data}. Aceita os dois para não quebrar se o backend embrulhar.
function desembrulhar<T>(body: unknown): T {
    if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
        const b = body as { success: boolean; data: T; error?: string };
        if (!b.success) throw new Error(b.error || 'Falha ao carregar o Analytics do Bot');
        return b.data;
    }
    return body as T;
}

async function get<T>(path: string, f: FiltrosBot, extra?: Record<string, unknown>): Promise<T> {
    const { data } = await apiClient.get(`/meta/analytics-bot${path}`, { params: toQuery(f, extra) });
    return desembrulhar<T>(data);
}

export const getResumoBot = (f: FiltrosBot) => get<ResumoBot>('/resumo', f);

export const getSerieBot = (f: FiltrosBot, granularidade: GranularidadeSerie) =>
    get<SerieBot>('/serie', f, { granularidade });

export const getFunisBot = (f: FiltrosBot) => get<FunisBot>('/funis', f);

export const getBroadcastsBot = (f: FiltrosBot) => get<BroadcastsBot>('/broadcasts', f);

export const getQuebrasBroadcastBot = (f: FiltrosBot, groupId: number) =>
    get<QuebrasBroadcast>(`/broadcasts/${groupId}/quebras`, f);

export const getRecortesBot = (f: FiltrosBot, dimensao: DimensaoRecorte, canal: CanalBot) =>
    get<RecortesBot>('/recortes', f, { dimensao, canal });
